/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import { getBotVersion, deleteBotVersion, startLiveRun, listBacktestRuns, updateBotVersionName, deleteBacktestRun, rerunBacktestRun, partialUpdateBotVersion } from '../../api/botsApi'; // Removed listBacktestConfigs and unused imports
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import LaunchBacktestModal from '../../components/bots/LaunchBacktestModal'; // Import the modal
import BacktestRunCard from '../../components/bots/BacktestRunCard'; // Import the new card component
import { APP_ACTION_SURFACES } from '../../access/surfaces';
import { useActionAccess } from '../../access/actionAccess';
import { formatStoredDate, formatStoredDateTime } from '../../utils/datetime';

const BotVersionDetailPage = () => {
  const { botId, versionId } = useParams();
  const navigate = useNavigate();
  const launchBacktestAccess = useActionAccess(APP_ACTION_SURFACES.botVersionLaunchBacktest);
  const rerunBacktestAccess = useActionAccess(APP_ACTION_SURFACES.botVersionRerunBacktest);
  const startLiveRunAccess = useActionAccess(APP_ACTION_SURFACES.botVersionStartLiveRun);
  const [version, setVersion] = useState(null);
  const [backtestRuns, setBacktestRuns] = useState([]); // State for backtest runs
  const [configIdToLabel, setConfigIdToLabel] = useState({}); // Map config id to label
  const [backtestProgress, setBacktestProgress] = useState({}); // State for backtest progress
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [selectedConfigForLaunch, setSelectedConfigForLaunch] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editableVersionName, setEditableVersionName] = useState("");
  const [rerunBusy, setRerunBusy] = useState({}); // runId -> boolean
  const [requireApproval, setRequireApproval] = useState(true);
  const [approvalSaving, setApprovalSaving] = useState(false);

  const fetchAllDetails = async () => { // Combined fetch function
    setIsLoading(true);
    setError(null);
    try {
      const versionData = await getBotVersion(versionId);
      setVersion(versionData); // State update 1
      setEditableVersionName(versionData.version_name || "");
      setRequireApproval(versionData.strategy_params?.require_approval ?? true);
      // Removed configsData as it's no longer needed

      try {
        const runsData = await listBacktestRuns({ bot_version_id: versionId }); // API call
        if (runsData && Array.isArray(runsData)) {
          setBacktestRuns(runsData);
          if (runsData.length === 0) {
            console.log(`No backtest runs found for version ${versionId} via API, though data might exist.`);
          }
        } else {
          console.warn(`Received unexpected data for backtest runs (version ${versionId}):`, runsData);
          setBacktestRuns([]);
        }
      } catch (runErr) {
        console.error(`Failed to fetch backtest runs for version ${versionId}:`, runErr);
        setError(prevError => prevError ? `${prevError}\nFailed to load backtest runs.` : 'Failed to load backtest runs.');
        setBacktestRuns([]);
      }

    } catch (err) {
      console.error(`Failed to fetch primary details for version ${versionId}:`, err);
      setError(err.message || `Failed to fetch details for version ${versionId}.`);
      // Clear all dependent states if primary fetches fail
      setVersion(null); 
      setBacktestRuns([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllDetails();
  }, [versionId]);

  // Open modal if linked with ?launch=1
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('launch') === '1' && launchBacktestAccess.allowed) {
      // Open the modal; it will fetch configs and preselect the first if available
      setShowLaunchModal(true);
    }
  }, [launchBacktestAccess.allowed, versionId]);

  useEffect(() => {
    const sockets = {};

    backtestRuns.forEach(run => {
      if (run.status === 'RUNNING' || run.status === 'PENDING') {
        // Construct WebSocket URL dynamically
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${protocol}//${window.location.host}/ws/backtest/${run.id}/`;
        
        const socket = new WebSocket(wsUrl);
        sockets[run.id] = socket;

        socket.onopen = () => {
          console.log(`WebSocket for run ${run.id} opened.`);
        };

        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Update progress
            if (typeof data.progress === 'number') {
              setBacktestProgress(prev => ({ ...prev, [run.id]: data.progress }));
            }

            // Update status in the main list if it has changed
            if (data.status && data.status !== run.status) {
              setBacktestRuns(prevRuns =>
                prevRuns.map(prevRun =>
                  prevRun.id === run.id ? { ...prevRun, status: data.status } : prevRun
                )
              );
            }

            // Close socket on final states
            if (data.status === 'COMPLETED' || data.status === 'FAILED') {
              socket.close();
            }
          } catch (e) {
            console.error('Failed to parse WebSocket message:', event.data, e);
          }
        };

        socket.onclose = (event) => {
          console.log(`WebSocket for run ${run.id} closed. Code: ${event.code}, Reason: ${event.reason}`);
          // Clean up progress state for this run
          setBacktestProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[run.id];
            return newProgress;
          });
          delete sockets[run.id];
        };

        socket.onerror = (error) => {
          console.error(`WebSocket error for run ${run.id}:`, error);
        };
      }
    });

    // Cleanup function to close all sockets when the component unmounts or dependencies change
    return () => {
      Object.values(sockets).forEach(socket => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.close();
        }
      });
    };
  }, [backtestRuns]); // Re-run this effect if the list of backtest runs changes

  const handleOpenLaunchModal = (configId) => {
    setSelectedConfigForLaunch(configId);
    setShowLaunchModal(true);
  };

  const handleCloseLaunchModal = () => {
    setShowLaunchModal(false);
    setSelectedConfigForLaunch(null);
  };

  const handleBacktestLaunched = (newRun) => {
    // Optionally, refresh the list of backtest runs or add the new run to the state
    setBacktestRuns(prevRuns => [newRun, ...prevRuns]); // Add to the top
    alert(`Backtest run ${newRun.id.substring(0,8)}... initiated successfully!`);
    // Could also navigate to a specific run details page if one exists
    // navigate(`/backtest-runs/${newRun.id}`);
  };

  const handleDeleteVersion = async () => {
    if (window.confirm(`Are you sure you want to delete this version (${versionId.substring(0,8)}...)? This action cannot be undone.`)) {
      setIsSubmitting(true);
      try {
        await deleteBotVersion(versionId);
        navigate(`/bots/${botId}`); // Navigate back to the bot detail page
      } catch (err) {
        console.error("Failed to delete bot version:", err);
        setError(err.message || "Failed to delete bot version. Please try again.");
        setIsSubmitting(false);
      }
      // No finally here, as navigation occurs on success
    }
  };

  const handleStartLiveRun = async () => {
    if (!startLiveRunAccess.ensureAccess()) {
      return;
    }
    if (window.confirm(`Are you sure you want to start a live run with this version (${versionId.substring(0,8)}...)?`)) {
      setIsSubmitting(true);
      setError(null);
      try {
        const liveRunData = await startLiveRun({ bot_version_id: versionId });
        // TODO: Navigate to the live run details page or show a success message
        alert(`Live run started successfully! Run ID: ${liveRunData.id}`);
        // navigate(`/live-runs/${liveRunData.id}`); // Example navigation
      } catch (err) {
        console.error("Failed to start live run:", err);
        setError(err.message || "Failed to start live run. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSaveName = async () => {
    if (editableVersionName === version.version_name) {
      setIsEditingName(false);
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const updatedVersion = await updateBotVersionName(versionId, editableVersionName);
      setVersion(updatedVersion);
      setEditableVersionName(updatedVersion.version_name);
      setIsEditingName(false);
    } catch (err) {
      console.error("Failed to update version name:", err);
      setError(err.message || "Failed to update name. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBacktestRun = async (runId) => {
    if (window.confirm(`Are you sure you want to delete this backtest run? This action cannot be undone.`)) {
      try {
        await deleteBacktestRun(runId);
        setBacktestRuns(backtestRuns.filter(r => r.id !== runId));
      } catch (err) {
        console.error("Failed to delete backtest run:", err);
        setError(err.message || "Failed to delete backtest run. Please try again.");
      }
    }
  };

  const handleRerunBacktestRun = async (runId) => {
    if (!rerunBacktestAccess.ensureAccess()) {
      return;
    }
    try {
      setRerunBusy(prev => ({ ...prev, [runId]: true }));
      const newRun = await rerunBacktestRun(runId, { preserve_random_seed: true });
      // Prepend new run to the list
      setBacktestRuns(prev => [newRun, ...prev]);
      // Optional toast/alert
      try { alert(`Re-run started: ${newRun.id.substring(0,8)}...`); } catch {}
    } catch (err) {
      console.error('Failed to re-run backtest:', err);
      setError(err.message || 'Failed to re-run backtest.');
    } finally {
      setRerunBusy(prev => ({ ...prev, [runId]: false }));
    }
  };

  const pageTitle = version ? version.version_name || 'Version' : "Loading Version Details...";

  const headerRightContent = version ? (
    <div className="space-x-2">
      <Link to={`/bots/${botId}/strategy-builder/${versionId}`}>
        <Button variant="outline" disabled={isSubmitting}>Edit Strategy</Button>
      </Link>
      <Link to={`/bots/${botId}/versions/${versionId}/backtest-configs/new`}>
        <Button variant="outline" disabled={isSubmitting}>Configure New Backtest</Button>
      </Link>
      {/* New: Launch Backtest at top; opens modal */}
      <Button
        variant="primary"
        onClick={() => {
          setShowLaunchModal(true);
        }}
        disabled={isSubmitting || launchBacktestAccess.disabled}
        title={launchBacktestAccess.disabled ? launchBacktestAccess.blockedMessage : undefined}
      >
        Launch Backtest
      </Button>
      <Button
        variant="primary"
        onClick={handleStartLiveRun}
        disabled={isSubmitting || startLiveRunAccess.disabled}
        title={startLiveRunAccess.disabled ? startLiveRunAccess.blockedMessage : undefined}
      >
        {isSubmitting ? 'Starting...' : 'Start Live Run'}
      </Button>
      <Button variant="destructive" onClick={handleDeleteVersion} disabled={isSubmitting}>
        {isSubmitting ? 'Deleting...' : 'Delete Version'}
      </Button>
    </div>
  ) : null;

  if (isLoading) {
    return (
      <PageLayout
        title={pageTitle}
        backButton={{ label: 'Back to Bot', to: `/bots/${botId}`, preferHistory: false, hideLabelOnMobile: true }}
      >
        <p>Loading...</p>
      </PageLayout>
    );
  }
  // Error display moved below to be within the main layout if version data partially loaded
  // if (!version && !isLoading) return <PageLayout title="Version Not Found"><p>The requested bot version could not be found.</p></PageLayout>;

  return (
    <PageLayout
      title={pageTitle}
      headerRightContent={headerRightContent}
      backButton={{ label: 'Back to Bot', to: `/bots/${botId}`, preferHistory: false, hideLabelOnMobile: true }}
    >
      {error && !version && <p className="text-destructive mb-4">Error loading version: {error}. If the version was deleted, it may no longer be accessible.</p>}
      {error && version && <p className="text-destructive mb-4">An error occurred: {error}</p>}
      {!version && !isLoading && !error && <p>The requested bot version could not be found.</p>}
      
      {version && (
      <div className="space-y-8">
        {/* Version Details Section - Full Width Row */}
        <section>
          <h2 className="text-xl font-semibold text-textPrimary mb-4">Version Details</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <strong className="text-lg">Name:</strong>
              {isEditingName ? (
                <Input
                  type="text"
                  value={editableVersionName}
                  onChange={(e) => setEditableVersionName(e.target.value)}
                  className="flex-grow"
                />
              ) : (
                <span className="text-lg">{version.version_name || "Not Set"}</span>
              )}
              <Button onClick={() => setIsEditingName(!isEditingName)} variant="outline" size="sm">
                {isEditingName ? 'Cancel' : 'Edit'}
              </Button>
              {isEditingName && (
                <Button onClick={handleSaveName} size="sm">
                  Save
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <p><strong>Date Created:</strong> {formatStoredDateTime(version.created_at, undefined, '-')}</p>
              <p><strong>Strategy Type:</strong> {version.strategy_name || 'N/A'}</p>
              <p><strong>Status:</strong> {version.is_active ? 'Active' : 'Inactive'}</p>
              <p><strong>Code Hash:</strong> {version.code_hash}</p>
            </div>
            {version.notes && <p className="mt-2"><strong>Notes:</strong> {version.notes}</p>}

            {/* Trade Approval Toggle */}
            <div className="flex items-center justify-between pt-2">
              <div>
                <p className="font-medium text-textPrimary">Require Trade Approval</p>
                <p className="text-xs text-textSecondary mt-0.5">When enabled, live trades from this bot will require manual approval before execution</p>
              </div>
              <button
                onClick={async () => {
                  const newVal = !requireApproval;
                  setRequireApproval(newVal);
                  setApprovalSaving(true);
                  try {
                    const strategyParams = { ...(version.strategy_params || {}), require_approval: newVal };
                    const updated = await partialUpdateBotVersion(versionId, { strategy_params: strategyParams });
                    setVersion(updated);
                  } catch (err) {
                    console.error('Failed to update approval setting:', err);
                    setRequireApproval(!newVal);
                    setError('Failed to update approval setting.');
                  } finally {
                    setApprovalSaving(false);
                  }
                }}
                disabled={approvalSaving}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                           ${requireApproval ? 'bg-primary' : 'bg-gray-600'}
                           ${approvalSaving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                             ${requireApproval ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </div>
          </div>
        </section>

        {/* Backtest Runs - Full width below configs */}
        <section className="mt-10">
          <h2 className="text-xl font-semibold text-textPrimary mb-4">Backtest Runs</h2>
          {backtestRuns.length === 0 ? (
            <p>No backtest runs found for this version yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-borderDefault">
                <thead className="bg-backgroundBody">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Description</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Symbol</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Timeframe</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Date Range</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Created At</th>
                    <th className={`px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider`}>Net P&L</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Total Trades</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Win Rate</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-backgroundSurface divide-y divide-borderDefault">
                  {backtestRuns.map(run => {
                    const stats = run.stats || {};
                    // Calculate win rate from stats.win_rate if available, else fallback to winning_trades/total_trades
                    let winRate = 'N/A';
                    if (typeof stats.win_rate === 'number') {
                      winRate = (stats.win_rate * 100).toFixed(2) + '%';
                    } else if (typeof stats.winning_trades === 'number' && typeof stats.total_trades === 'number' && stats.total_trades > 0) {
                      winRate = ((stats.winning_trades / stats.total_trades) * 100).toFixed(2) + '%';
                    }
                    // Use any available timeframe field from API (support legacy and new field names)
                    const timeframe = run.timeframe || run.run_timeframe || run.original_timeframe || 'N/A';
                    return (
                      <tr key={run.id}>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-primary">
                          <Link to={`/backtest-runs/${run.id}`}>
                            <Button variant="outline" size="sm">{run.description || configIdToLabel[run.config] || run.config.substring(0,8) + '...'}</Button>
                          </Link>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{run.instrument_symbol || 'N/A'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{timeframe}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{run.data_window_start && run.data_window_end ? `${formatStoredDate(run.data_window_start, undefined, '-')} - ${formatStoredDate(run.data_window_end, undefined, '-')}` : 'N/A'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{formatStoredDateTime(run.created_at, undefined, 'N/A')}</td>
                        <td className={`${stats.net_pnl >= 0 ? 'text-green-600' : 'text-red-600'} px-3 py-2 whitespace-nowrap text-xs`}>{stats.net_pnl !== undefined ? stats.net_pnl.toFixed(2) : 'N/A'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{stats.total_trades !== undefined ? stats.total_trades : 'N/A'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{winRate}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleRerunBacktestRun(run.id)}
                              disabled={!!rerunBusy[run.id] || rerunBacktestAccess.disabled}
                              title={rerunBacktestAccess.disabled ? rerunBacktestAccess.blockedMessage : undefined}
                            >
                              {rerunBusy[run.id] ? 'Re-running…' : 'Re-run'}
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => handleDeleteBacktestRun(run.id)}>Delete</Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
        {/* TODO: Section for Live Runs */}
      </div>
      )}
      {showLaunchModal && (
        <LaunchBacktestModal
          botVersionId={versionId}
          backtestConfigId={selectedConfigForLaunch}
          onSuccess={handleBacktestLaunched}
          onClose={() => {
            setShowLaunchModal(false);
            setSelectedConfigForLaunch(null);
          }}
        />
      )}
    </PageLayout>
  );
};

export default BotVersionDetailPage;
